import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import {
  CATEGORIES,
  PLATFORMS,
  VISIBLE_PLATFORMS,
  SORT_OPTIONS,
  PlatformGlyph,
  ShieldCheckIcon,
  BadgeCheckIcon
} from '../../pages/marketplaceShared';

const SearchGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M21 21l-4.2-4.2m1.2-5.3a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function PlatformPillRow({ activePlatforms, setActivePlatforms }) {
  const [expanded, setExpanded] = React.useState(false);
  const hiddenCount = Math.max(0, VISIBLE_PLATFORMS.length - 3);
  const visiblePlatforms = expanded ? VISIBLE_PLATFORMS : VISIBLE_PLATFORMS.slice(0, 3);

  return (
    <div className={`target-pill-row platform-pills ${expanded ? 'is-expanded' : ''}`}>
      {visiblePlatforms.map(platform => (
        <button
          type="button"
          key={platform}
          className={`target-pill platform-toggle ${activePlatforms.includes(platform) ? 'active' : ''}`}
          aria-pressed={activePlatforms.includes(platform)}
          onClick={() => setActivePlatforms(prev => (
            prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
          ))}
        >
          <PlatformGlyph platform={platform} />
          {platform}
        </button>
      ))}
      {!expanded && hiddenCount > 0 && (
        <button type="button" className="target-pill platform-more" onClick={() => setExpanded(true)}>
          +{hiddenCount}
        </button>
      )}
    </div>
  );
}

export default function FilterPanel({
  activePlatforms,
  setActivePlatforms,
  searchName,
  setSearchName,
  activeCategory,
  setActiveCategory,
  monetizedOnly,
  setMonetizedOnly,
  verifiedOnly,
  setVerifiedOnly,
  followerRange,
  setFollowerRange,
  priceRange,
  setPriceRange,
  incomeRange,
  setIncomeRange,
  sortBy,
  setSortBy,
  onClear,
  onSearch,
  className = ''
}) {
  return (
    <section className={`target-filter-panel target-filter-bar design-filter-panel ${className}`.trim()} aria-label="Marketplace filters">
      <div className="filter-bar">
        <form
          className="search-field"
          onSubmit={event => {
            event.preventDefault();
            onSearch?.();
          }}
        >
          <SearchGlyph />
          <input
            type="search"
            placeholder="Search by community name, tag, or niche..."
            aria-label="Search by community name, tag, or niche"
            value={searchName}
            onChange={event => setSearchName(event.target.value)}
          />
          <button type="submit" className="search-submit" aria-label="Search">
            <SearchGlyph />
          </button>
        </form>
        <label className="select-filter">
          <span className="filter-select-icon"><SlidersHorizontal size={15} /></span>
          <select
            value={activePlatforms.length === 1 ? activePlatforms[0] : 'All'}
            onChange={event => setActivePlatforms(event.target.value === 'All' ? [] : [event.target.value])}
            aria-label="Filter by network"
          >
            {PLATFORMS.map(platform => (
              <option key={platform} value={platform}>{platform === 'All' ? 'All networks' : platform}</option>
            ))}
          </select>
        </label>
        <label className="select-filter">
          <select
            value={activeCategory}
            onChange={event => setActiveCategory(event.target.value)}
            aria-label="Filter by category"
          >
            {CATEGORIES.map(category => (
              <option key={category} value={category}>{category === 'All' ? 'All categories' : category}</option>
            ))}
          </select>
        </label>
        <label className="sort-filter">
          <span>Sort:</span>
          <select
            value={sortBy}
            onChange={event => setSortBy(event.target.value)}
            aria-label="Sort listings"
          >
            {SORT_OPTIONS.map(option => (
              <option key={option} value={option}>{option === 'Newest first' ? 'Recently listed' : option}</option>
            ))}
          </select>
        </label>
      </div>

      <details className="advanced-filters">
        <summary>Advanced filters</summary>
        <div className="advanced-filter-content">
          <button type="button" className="clear-filters" onClick={onClear}>Clear all filters</button>
          <PlatformPillRow activePlatforms={activePlatforms} setActivePlatforms={setActivePlatforms} />
          <div className="target-filter-row filter-row-search">
            <button
              type="button"
              className={`target-toggle-chip ${monetizedOnly ? 'active' : ''}`}
              onClick={() => setMonetizedOnly(value => !value)}
              aria-pressed={monetizedOnly}
            >
              <ShieldCheckIcon /> YouTube monetized
            </button>
            <button
              type="button"
              className={`target-toggle-chip ${verifiedOnly ? 'active' : ''}`}
              onClick={() => setVerifiedOnly(value => !value)}
              aria-pressed={verifiedOnly}
            >
              <BadgeCheckIcon /> Verified identity
            </button>
          </div>
          <div className="target-filter-row filter-row-ranges">
            <section className="target-filter-group">
              <h3>Subscribers</h3>
              <div className="target-range-inputs">
                <label className="target-range-field">
                  <span>from</span>
                  <input type="number" min="0" placeholder="0" aria-label="Minimum subscribers" value={followerRange.min} onChange={event => setFollowerRange(prev => ({ ...prev, min: event.target.value }))} />
                </label>
                <label className="target-range-field">
                  <span>to</span>
                  <input type="number" min="0" placeholder="Any" aria-label="Maximum subscribers" value={followerRange.max} onChange={event => setFollowerRange(prev => ({ ...prev, max: event.target.value }))} />
                </label>
              </div>
            </section>
            <section className="target-filter-group">
              <h3>Price</h3>
              <div className="target-range-inputs">
                <label className="target-range-field">
                  <span>from</span>
                  <input type="number" min="0" placeholder="0" aria-label="Minimum price" value={priceRange.min} onChange={event => setPriceRange(prev => ({ ...prev, min: event.target.value }))} />
                </label>
                <label className="target-range-field">
                  <span>to</span>
                  <input type="number" min="0" placeholder="Any" aria-label="Maximum price" value={priceRange.max} onChange={event => setPriceRange(prev => ({ ...prev, max: event.target.value }))} />
                </label>
              </div>
            </section>
            <section className="target-filter-group">
              <h3>Income</h3>
              <div className="target-range-inputs">
                <label className="target-range-field">
                  <span>from</span>
                  <input type="number" min="0" placeholder="0" aria-label="Minimum income" value={incomeRange.min} onChange={event => setIncomeRange(prev => ({ ...prev, min: event.target.value }))} />
                </label>
                <label className="target-range-field">
                  <span>to</span>
                  <input type="number" min="0" placeholder="Any" aria-label="Maximum income" value={incomeRange.max} onChange={event => setIncomeRange(prev => ({ ...prev, max: event.target.value }))} />
                </label>
              </div>
            </section>
          </div>
        </div>
      </details>
    </section>
  );
}
